import { useEffect, useRef, useState, type RefObject } from 'react';
import { tokenStorage, API_BASE } from '@/services/apiClient';
import { sportsApi, type SportsDashInit } from '@/services/sportsApi';
import { isScreenLimitApiError } from '@/services/screenLimit';
import { useAppDispatch } from '@/store';
import { screenLimitReached } from '@/store/slices/screenSlice';
import type { SportsProviderName } from '@/types/sports';

/**
 * Minimal Shaka surface we use. Imported dynamically so the ~600KB UMD
 * bundle stays out of the main chunk — only viewers who hit a DASH event
 * pay the cost.
 */
interface ShakaNetworkingEngine {
  registerRequestFilter: (
    fn: (type: number, request: { uris: string[]; headers: Record<string, string> }) => void,
  ) => void;
}
interface ShakaPlayer {
  attach: (video: HTMLVideoElement) => Promise<void>;
  configure: (config: unknown) => boolean;
  load: (manifestUri: string) => Promise<void>;
  destroy: () => Promise<void>;
  getNetworkingEngine: () => ShakaNetworkingEngine | null;
  addEventListener: (event: 'error', cb: (evt: { detail: { code: number; data?: unknown[] } }) => void) => void;
}
interface ShakaModule {
  Player: new () => ShakaPlayer;
  polyfill: { installAll: () => void };
  net: { NetworkingEngine: { RequestType: { MANIFEST: number } } };
}

let shakaModulePromise: Promise<ShakaModule> | null = null;
function loadShaka(): Promise<ShakaModule> {
  if (!shakaModulePromise) {
    shakaModulePromise = import('shaka-player/dist/shaka-player.compiled')
      .then((mod) => (mod.default ?? mod) as unknown as ShakaModule);
  }
  return shakaModulePromise;
}

interface UseShakaPlayerArgs {
  /** Sports event id — backend opens a DASH session keyed on this. */
  eventId: string;
  /** Sports provider (must be one of DASH_SPORTS_PROVIDERS for this hook to fire). */
  source: SportsProviderName | null;
  /** Optional streamNo for providers that expose multiple variants per channel. */
  streamNo?: number;
  videoRef: RefObject<HTMLVideoElement>;
  /** Bumping this re-runs the effect — Watch's manual "Retry" path. */
  retryToken?: number;
  /** Re-keys the effect when the underlying playlist (or source preference) changes. */
  key?: string;
}

interface UseShakaPlayerResult {
  /** Fatal error code/category from Shaka — `null` until something goes wrong. */
  fatal: string | null;
  /**
   * True when the failure is a network/HTTP error (Shaka category 1, e.g.
   * 1001 BAD_HTTP_STATUS) — the manifest or a segment came back non-2xx.
   * For bintvdrm this means the channel's upstream is currently dead/off-air
   * (e.g. an expired DAZN session), so the caller can show a friendly
   * "channel unavailable" message instead of a raw error code.
   */
  unavailable: boolean;
  /** Whether the player is currently loading the manifest. */
  loading: boolean;
}

/** Shaka error category 1 = NETWORK (1001 BAD_HTTP_STATUS, 1002 HTTP_ERROR, 1003 TIMEOUT, …). */
function isNetworkError(code: unknown): boolean {
  return typeof code === 'number' && Math.floor(code / 1000) === 1;
}

/**
 * Shaka.Player bootstrap for the bintvdrm DASH+ClearKey playback path.
 * Parallels useHlsPlayer: opens one Shaka instance per (eventId, source)
 * pair, attaches to the video element, fetches the dash-init config,
 * configures clearKeys, and loads the manifest. Tears down on unmount
 * or key change.
 *
 * Auth: Shaka's networking engine fetches the MPD itself, so we register
 * a request filter to attach the Bearer token on MANIFEST requests. The
 * segment proxy at /api/sports-dash-seg/.../.../ is intentionally
 * anonymous (the HMAC sig in the URL is the auth tag) so segment fetches
 * don't need the header.
 */
export function useShakaPlayer({
  eventId,
  source,
  streamNo,
  videoRef,
  retryToken = 0,
  key = `${eventId}|${source ?? 'auto'}`,
}: UseShakaPlayerArgs): UseShakaPlayerResult {
  const dispatch = useAppDispatch();
  const playerRef = useRef<ShakaPlayer | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!source) return;
    if (!videoRef.current) return;

    let active = true;
    setFatal(null);
    setUnavailable(false);
    setLoading(true);
    let player: ShakaPlayer | null = null;

    (async (): Promise<void> => {
      try {
        // 1. Load Shaka + polyfills. polyfill.installAll() patches MediaSource /
        //    EME APIs so older browsers that ship buggy versions still work.
        const shaka = await loadShaka();
        if (!active) return;
        shaka.polyfill.installAll();

        // 2. Open the DASH session — backend returns manifestUrl + clearKeys.
        let init: SportsDashInit;
        try {
          init = await sportsApi.dashInit(eventId, source, streamNo);
        } catch (err) {
          if (!active) return;
          // dash-init is the DASH playback-START (goes through api()), so a
          // screen-limit block arrives as an ApiClientError. Surface it via the
          // global modal rather than the generic fatal overlay.
          if (isScreenLimitApiError(err)) {
            dispatch(screenLimitReached());
            setFatal('screenLimit');
            setLoading(false);
            return;
          }
          setFatal(`dash-init failed: ${(err as Error).message ?? 'unknown'}`);
          setLoading(false);
          return;
        }
        if (!active) return;

        // 3. Construct Shaka.Player and configure ClearKey DRM.
        player = new shaka.Player();
        playerRef.current = player;

        // ClearKey config: Shaka accepts a {keyId: keyBase64} map under
        // drm.clearKeys (NOT a Widevine license URL). Keys arrive from the
        // backend as 32-char lowercase hex — feed them through as-is; Shaka
        // detects hex vs base64 internally.
        player.configure({
          drm: { clearKeys: init.clearKeys },
        });

        // 4. Attach to <video> and register the Bearer-auth filter for
        //    manifest requests. Segments hit /api/sports-dash-seg which is
        //    sig-authed (no Bearer needed) so we filter only MANIFEST.
        const networking = player.getNetworkingEngine();
        if (networking) {
          const ManifestType = shaka.net.NetworkingEngine.RequestType.MANIFEST;
          networking.registerRequestFilter((type, request) => {
            if (type !== ManifestType) return;
            const token = tokenStorage.get();
            if (token) request.headers['Authorization'] = `Bearer ${token}`;
          });
        }

        // Error listener fires for both transient + fatal — Shaka has no
        // ".fatal" distinction in the v4+ API. Surface anything that comes
        // through; the caller (WatchSports) shows the retry overlay.
        player.addEventListener('error', (evt) => {
          if (!active) return;
          const code = evt.detail?.code;
          console.error('[Shaka] error', code, evt.detail?.data);
          setFatal(`shaka error ${code}`);
          if (isNetworkError(code)) setUnavailable(true);
        });

        await player.attach(videoRef.current!);
        if (!active) {
          await player.destroy();
          return;
        }

        // 5. Load the manifest. manifestUrl is a relative path like
        //    `/api/sports-stream/:id/manifest.mpd?source=bintvdrm&streamNo=1`
        //    — resolve against API_BASE so Shaka requests the right host.
        const absoluteUrl = init.manifestUrl.startsWith('http')
          ? init.manifestUrl
          : `${API_BASE}${init.manifestUrl}`;
        await player.load(absoluteUrl);
        if (!active) return;
        setLoading(false);
      } catch (err) {
        if (!active) return;
        const code = (err as { code?: number }).code;
        const msg = code
          ? `shaka error ${code}`
          : ((err as Error).message ?? 'shaka load failed');
        console.error('[Shaka] load failed', err);
        setFatal(msg);
        if (isNetworkError(code)) setUnavailable(true);
        setLoading(false);
      }
    })();

    return (): void => {
      active = false;
      if (player) {
        // Shaka.destroy is async — fire and forget. We've already nulled the
        // ref so a re-run won't see this instance.
        player.destroy().catch(() => { /* ignore teardown errors */ });
      }
      playerRef.current = null;
    };
  }, [key, retryToken, eventId, source, streamNo, videoRef]);

  return { fatal, unavailable, loading };
}
